import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { AccountingHistoryPreset, AccountingHistoryResponse } from '@pokerroom/types';
import { api } from '../../lib/api';
import { sessionVenueKey, useAuthStore } from '../../store/auth.store';
import { formatAccountingInstant } from '../../lib/accounting-time';
import { AccountingOverviewSummary } from '../accounting/AccountingOverviewSummary';
import { gameRows } from '../accounting/accountingReportModel';

type Venue = { id: string; name: string; code: string };
function identity() {
  const s = useAuthStore.getState();
  return JSON.stringify([
    s.user?.id,
    sessionVenueKey(s.accessToken),
    s.sessionRevision,
    s.allowedActions,
    s.visiblePages,
  ]);
}
export default function VenueAccountingOverviewTab({ venues }: { venues: Venue[] }) {
  const scope = useAuthStore(() => identity());
  const [selected, setSelected] = useState('');
  const target = venues.some((v) => v.id === selected) ? selected : venues[0]?.id;
  const { t } = useTranslation('admin');
  return (
    <section className="space-y-4">
      <h1 className="admin-page-title">{t('venue.tabAccounting')}</h1>
      <nav className="flex flex-wrap gap-2" aria-label={t('venue.venueName')}>
        {venues.map((v) => (
          <button
            type="button"
            className="admin-button"
            aria-pressed={target === v.id}
            key={v.id}
            onClick={() => setSelected(v.id)}
          >
            {v.name} · {v.code}
          </button>
        ))}
      </nav>
      {target && <TargetOverview key={`${scope}:${target}`} scope={scope} venueId={target} />}
    </section>
  );
}
function TargetOverview({ scope, venueId }: { scope: string; venueId: string }) {
  const { t, i18n } = useTranslation('accounting');
  const qc = useQueryClient();
  const [preset, setPreset] = useState<AccountingHistoryPreset | ''>('current_week');
  const [date, setDate] = useState('');
  const [day, setDay] = useState('');
  const [page, setPage] = useState(1);
  const [stale, setStale] = useState(false);
  const alive = useRef(true);
  const prefix = ['venue-accounting-history', scope, venueId];
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      void qc.cancelQueries({ queryKey: prefix });
      qc.removeQueries({ queryKey: prefix });
    };
  }, [qc, scope, venueId]); // eslint-disable-line react-hooks/exhaustive-deps
  const selectors = date ? { date } : preset ? { preset } : {};
  const read = async (
    signal: AbortSignal,
    detailDate?: string,
    snapshot?: AccountingHistoryResponse,
  ) => {
    const result = await api.get<AccountingHistoryResponse>(
      `/accounting/venues/${venueId}/history${detailDate ? `/${detailDate}` : ''}`,
      {
        signal,
        params: {
          ...selectors,
          ...(snapshot
            ? { sourceVersion: snapshot.sourceVersion, asOf: snapshot.asOf, page, limit: 20 }
            : {}),
        },
      },
    );
    if (!alive.current || signal.aborted || identity() !== scope || result.venueId !== venueId)
      throw Error('REPORT_IDENTITY_CHANGED');
    return result;
  };
  const summary = useQuery({
    staleTime: 0,
    queryKey: [...prefix, 'summary', selectors, null, null, 1, 20],
    queryFn: ({ signal }) => read(signal),
    retry: false,
    refetchOnWindowFocus: 'always',
  });
  const data = !summary.isError && summary.data?.venueId === venueId ? summary.data : undefined;
  const detailQuery = useQuery({
    staleTime: 0,
    queryKey: [...prefix, 'detail', selectors, data?.sourceVersion, data?.asOf, day, page, 20],
    queryFn: ({ signal }) => read(signal, day, data),
    enabled: !!day && !!data,
    retry: false,
    refetchOnWindowFocus: 'always',
  });
  const detail =
    !detailQuery.isError &&
    detailQuery.data?.venueId === venueId &&
    detailQuery.data.sourceVersion === data?.sourceVersion &&
    detailQuery.data.asOf === data?.asOf
      ? detailQuery.data.detail
      : null;
  useEffect(() => {
    const error = detailQuery.error as { response?: { status?: number } } | null;
    if (error?.response?.status !== 409) return;
    setStale(true);
    setDay('');
    setPage(1);
    qc.removeQueries({ queryKey: [...prefix, 'detail'] });
    void summary.refetch();
  }, [detailQuery.error]); // eslint-disable-line react-hooks/exhaustive-deps
  const change = () => {
    setDay('');
    setPage(1);
    setStale(false);
  };
  const button = 'min-h-11 rounded border border-base-300 px-3 py-2 disabled:opacity-50';
  const cell = 'p-3 whitespace-nowrap text-right tabular-nums';
  const denied = [summary.error, detailQuery.error].some(
    (e) => (e as { response?: { status?: number } } | null)?.response?.status === 403,
  );
  return (
    <div className="space-y-4">
      <p>{t('overview.readOnlyVenueNotice')}</p>
      <div className="flex flex-wrap items-end gap-3">
        {(['current_week', 'previous_week', 'current_month'] as const).map((p) => (
          <button
            type="button"
            key={p}
            aria-pressed={!date && preset === p}
            className={button}
            onClick={() => {
              change();
              setDate('');
              setPreset(p);
            }}
          >
            {t(`overview.${p}`)}
          </button>
        ))}
        <label className="flex flex-col gap-1">
          {t('overview.date')}
          <input
            type="date"
            value={date}
            className="input input-bordered"
            onChange={(e) => {
              change();
              setDate(e.target.value);
              setPreset('');
            }}
          />
        </label>
        <button
          type="button"
          className={button}
          disabled={summary.isFetching}
          onClick={() => {
            change();
            void summary.refetch();
          }}
        >
          {t('dailyLedgers.refresh')}
        </button>
      </div>
      {summary.isFetching && <p role="status">{t('dailyLedgers.loading')}</p>}
      {denied ? (
        <p role="alert">{t('dailyLedgers.denied')}</p>
      ) : (
        summary.isError && <p role="alert">{t('overview.failed')}</p>
      )}
      {stale && <p role="alert">{t('overview.stale')}</p>}
      {!denied && data && (
        <>
          <p className="text-sm">
            {t('overview.serverBusinessDate')}: {data.serverBusinessDate} · {t('overview.asOf')}:{' '}
            {formatAccountingInstant(data.asOf, data.displayTimezone, i18n.language)} ·{' '}
            {data.displayTimezone}
          </p>
          <AccountingOverviewSummary
            data={data}
            selectedDate={day}
            onSelectDay={(selected) => {
              setDay(selected);
              setPage(1);
              setStale(false);
            }}
          />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <caption>{t('overview.units')}</caption>
              <thead>
                <tr>
                  <th className={cell}>{t('overview.date')}</th>
                  <th className={cell}>{t('overview.revenue')} (TWD)</th>
                  <th className={cell}>OP</th>
                </tr>
              </thead>
              <tbody>
                {data.days.map((d) => (
                  <tr key={d.businessDate}>
                    <th className={cell}>{d.businessDate}</th>
                    {[d.revenue, d.op].map((v, i) => (
                      <td key={i} className={cell}>
                        {d.status === 'NOT_YET' ? '—' : (v?.value ?? t('overview.unavailable'))}
                        {d.status !== 'NOT_YET' && v?.status === 'PARTIAL' && (
                          <small className="block text-warning">{t('overview.PARTIAL')}</small>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {day && (
            <section
              className="space-y-3 border border-base-300 p-3"
              aria-label={t('overview.detail')}
            >
              <div className="flex items-center justify-between">
                <h2>
                  {day} · {t('overview.detail')}
                </h2>
                <button type="button" className={button} onClick={change}>
                  {t('overview.close')}
                </button>
              </div>
              {detailQuery.isFetching && <p role="status">{t('dailyLedgers.loading')}</p>}
              {detailQuery.isError && <p role="alert">{t('overview.failed')}</p>}
              {detail && (
                <>
                  {gameRows(detail.settlements).map((g) => (
                    <p key={g.tournamentId}>
                      {g.sessionLabel} · {t(`overview.${g.status}`)} · {g.people ?? '—'} ·{' '}
                      {g.netAward ?? '—'} TWD
                      {g.unverified && (
                        <span className="text-warning"> · {t('overview.sourceUnverified')}</span>
                      )}
                    </p>
                  ))}
                  {detail.groups.map((g) => (
                    <section key={g.key}>
                      <h3>
                        {t(`dailyLedgers.groups.${g.key}`)} · {g.unit} · {t(`overview.${g.status}`)}
                      </h3>
                      <p>
                        {t('overview.groupTotals', {
                          opening: g.opening ?? '—',
                          increases: g.increases ?? '—',
                          decreases: g.decreases ?? '—',
                          closing: g.closing ?? '—',
                          total: g.total,
                        })}
                      </p>
                      {g.warnings.map((w) => (
                        <p key={w} className="text-warning">
                          {t(`dailyLedgers.warnings.${w}`, {
                            defaultValue: t('dailyLedgers.unavailable'),
                          })}
                        </p>
                      ))}
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <tbody>
                            {g.rows.map((row) => (
                              <tr key={row.id}>
                                <td className="p-3">{row.memberName ?? row.kind}</td>
                                <td className={cell}>
                                  {row.amount ?? '—'} {row.unit}
                                </td>
                                <td className={cell}>
                                  {formatAccountingInstant(
                                    row.postedAt,
                                    data.displayTimezone,
                                    i18n.language,
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  ))}
                  <section>
                    <h3>{t('overview.receipts')}</h3>
                    {detail.receipts.map((r) => (
                      <p key={r.id}>
                        {t(`overview.${r.direction}`)} · {r.amount} {r.unit}
                      </p>
                    ))}
                  </section>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      className={button}
                      disabled={page <= 1 || detailQuery.isFetching}
                      onClick={() => setPage((n) => n - 1)}
                    >
                      {t('overview.previousPage')}
                    </button>
                    <span>{page}</span>
                    <button
                      type="button"
                      className={button}
                      disabled={
                        detailQuery.isFetching ||
                        ![
                          ...detail.groups.map((g) => g.total),
                          detail.totals.receipts,
                          detail.totals.income,
                        ].some((n) => n > page * 20)
                      }
                      onClick={() => setPage((n) => n + 1)}
                    >
                      {t('overview.nextPage')}
                    </button>
                  </div>
                </>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}
